import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Plus, Check, X, Edit2, Users, Navigation } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../utils/api';
import { useApp } from '../../context/AppContext';
import Modal from '../../components/common/Modal';
import { useToast } from '../../components/common/Toast';

function PickupPoints() {
  const { currentUser } = useAuth();
  const { routes, students, setRoutes } = useApp();
  const toast = useToast();
  const isAdmin = currentUser?.role === 'admin';

  const [points, setPoints]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  const [addModal, setAddModal]       = useState(false);
  const [approveModal, setApproveModal] = useState(null);
  const [assignModal, setAssignModal]   = useState(null);
  const [editModal, setEditModal]       = useState(null);

  const [gpsLoading, setGpsLoading] = useState(false);
  const [addForm, setAddForm] = useState({ routeId: '', name: '', latitude: '', longitude: '' });
  const [approveForm, setApproveForm] = useState({ transportFee: '', name: '' });
  const [assignStudentId, setAssignStudentId] = useState('');
  const [studentSearch, setStudentSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.list('pickup-points');
      setPoints(data || []);
    } catch {
      toast?.error('Failed to load pickup points');
    }
    setLoading(false);
  }, []); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const getLocation = () => {
    if (!navigator.geolocation) {
      toast?.error('Geolocation not supported on this device');
      return;
    }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setAddForm(f => ({
          ...f,
          latitude: pos.coords.latitude.toFixed(7),
          longitude: pos.coords.longitude.toFixed(7),
        }));
        setGpsLoading(false);
      },
      () => {
        toast?.error('Location access denied. Please allow location in browser settings.');
        setGpsLoading(false);
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  const openAdd = () => {
    const driverRoute = isAdmin ? '' : (routes[0]?.id || '');
    setAddForm({ routeId: driverRoute, name: '', latitude: '', longitude: '' });
    setAddModal(true);
  };

  const savePoint = async () => {
    if (!addForm.name.trim()) { toast?.error('Enter a location name'); return; }
    if (!addForm.latitude || !addForm.longitude) { toast?.error('Capture GPS location first'); return; }
    if (!addForm.routeId) { toast?.error('Select a route'); return; }
    try {
      await api.create('pickup-points', addForm);
      toast?.success('Pickup point submitted for admin approval');
      setAddModal(false);
      load();
    } catch (e) {
      toast?.error(e.message || 'Failed to save');
    }
  };

  const approvePoint = async () => {
    if (!approveForm.transportFee) { toast?.error('Enter transport fee'); return; }
    try {
      await api.create(`pickup-points/${approveModal.id}/approve`, {
        transportFee: Number(approveForm.transportFee),
        name: approveForm.name || undefined,
      });
      toast?.success('Pickup point approved');
      setApproveModal(null);
      load();
      // Refresh routes in AppContext so Routes page shows the new stop immediately
      api.list('transport/routes').then(r => setRoutes(r || [])).catch(() => {});
    } catch (e) {
      toast?.error(e.message);
    }
  };

  const rejectPoint = async (pt) => {
    if (!window.confirm(`Reject pickup point "${pt.name}"?`)) return;
    try {
      await api.create(`pickup-points/${pt.id}/reject`, {});
      toast?.success('Rejected');
      load();
    } catch (e) {
      toast?.error(e.message);
    }
  };

  const assignStudent = async () => {
    if (!assignStudentId) { toast?.error('Select a student'); return; }
    try {
      await api.create(`pickup-points/${assignModal.id}/assign`, { studentId: assignStudentId });
      toast?.success('Student assigned successfully');
      setAssignModal(null);
      setAssignStudentId('');
      setStudentSearch('');
    } catch (e) {
      toast?.error(e.message);
    }
  };

  const saveEdit = async () => {
    if (!editModal.name.trim()) { toast?.error('Name cannot be empty'); return; }
    try {
      await api.update('pickup-points', editModal.id, { name: editModal.name });
      toast?.success('Name updated');
      setEditModal(null);
      load();
    } catch (e) {
      toast?.error(e.message);
    }
  };

  const deletePoint = async (pt) => {
    if (!window.confirm(`Delete "${pt.name}"?`)) return;
    try {
      await api.remove('pickup-points', pt.id);
      toast?.success('Deleted');
      load();
    } catch (e) {
      toast?.error(e.message);
    }
  };

  const filtered = activeTab === 'pending'
    ? points.filter(p => p.status === 'pending')
    : points;

  const pendingCount = points.filter(p => p.status === 'pending').length;

  const filteredStudents = students.filter(s =>
    s.status === 'active' &&
    (!studentSearch || `${s.firstName} ${s.lastName}`.toLowerCase().includes(studentSearch.toLowerCase()))
  );

  const statusBadge = (status) => {
    const map = {
      approved: 'bg-green-100 text-green-700',
      rejected:  'bg-red-100 text-red-600',
      pending:   'bg-yellow-100 text-yellow-700',
    };
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${map[status] || 'bg-gray-100 text-gray-600'}`}>{status}</span>;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Pickup Points</h1>
          <p className="text-gray-500 text-sm">{points.length} total · {pendingCount} pending</p>
        </div>
        <button onClick={openAdd}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium">
          <Plus size={16}/> Add Pickup Point
        </button>
      </div>

      {/* Driver: show assigned route info */}
      {!isAdmin && routes.length > 0 && (
        <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-xl flex items-center gap-3">
          <Navigation size={18} className="text-purple-600 flex-shrink-0"/>
          <div>
            <p className="text-sm font-semibold text-purple-800">Your Assigned Route</p>
            <p className="text-sm text-purple-700">{routes[0]?.name} · {routes[0]?.startPoint} → {routes[0]?.endPoint}</p>
          </div>
        </div>
      )}
      {!isAdmin && routes.length === 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-xl text-sm text-yellow-700">
          No route assigned to you yet. Contact admin to assign a route.
        </div>
      )}

      {/* Admin tabs */}
      {isAdmin && (
        <div className="flex gap-2 mb-4">
          {[['all', 'All Points'], ['pending', `Pending Approval${pendingCount > 0 ? ` (${pendingCount})` : ''}`]].map(([t, label]) => (
            <button key={t} onClick={() => setActiveTab(t)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium ${activeTab === t ? 'bg-purple-600 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
              {label}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin"/>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <MapPin size={44} className="mx-auto mb-3 opacity-25"/>
          <p className="font-medium">No pickup points {activeTab === 'pending' ? 'pending approval' : 'found'}</p>
          <p className="text-sm mt-1">
            {!isAdmin ? 'Click "Add Pickup Point" to submit your current GPS location' : ''}
          </p>
        </div>
      ) : (
        <div className="grid gap-3">
          {filtered.map(pt => (
            <div key={pt.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <MapPin size={15} className="text-purple-600 flex-shrink-0"/>
                    <span className="font-semibold text-gray-800">{pt.name}</span>
                    {statusBadge(pt.status)}
                  </div>
                  <div className="text-sm text-gray-500 space-y-0.5 ml-5">
                    <p>Route: <span className="text-gray-700 font-medium">{pt.routeName || pt.routeId}</span></p>
                    {isAdmin && <p>Driver: <span className="text-gray-700">{pt.driverName || '—'}</span></p>}
                    <p className="font-mono text-xs text-gray-400">{pt.latitude}, {pt.longitude}</p>
                    {pt.transportFee > 0 && (
                      <p>Fee: <span className="text-green-700 font-semibold">₹{pt.transportFee}</span></p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {/* Driver: edit name if pending */}
                  {!isAdmin && pt.status === 'pending' && (
                    <button onClick={() => setEditModal({ ...pt })}
                      className="p-1.5 hover:bg-purple-50 rounded-lg text-purple-600" title="Edit name">
                      <Edit2 size={15}/>
                    </button>
                  )}
                  {/* Admin actions */}
                  {isAdmin && pt.status === 'pending' && (
                    <>
                      <button
                        onClick={() => { setApproveModal(pt); setApproveForm({ transportFee: '', name: pt.name }); }}
                        className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700">
                        <Check size={13}/> Approve
                      </button>
                      <button onClick={() => rejectPoint(pt)}
                        className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white text-xs rounded-lg hover:bg-red-600">
                        <X size={13}/> Reject
                      </button>
                    </>
                  )}
                  {isAdmin && pt.status === 'approved' && (
                    <button
                      onClick={() => { setAssignModal(pt); setAssignStudentId(''); setStudentSearch(''); }}
                      className="flex items-center gap-1 px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700">
                      <Users size={13}/> Assign Student
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => deletePoint(pt)}
                      className="p-1.5 hover:bg-red-50 rounded-lg text-red-400 ml-1" title="Delete">
                      <X size={15}/>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Add Pickup Point Modal ── */}
      <Modal isOpen={addModal} onClose={() => setAddModal(false)} title="Add Pickup Point" size="md">
        <div className="space-y-4">
          {/* Route selection */}
          {isAdmin ? (
            <div>
              <label className="block text-sm font-medium mb-1">Route</label>
              <select value={addForm.routeId} onChange={e => setAddForm(f => ({...f, routeId: e.target.value}))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                <option value="">Select Route</option>
                {routes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
            </div>
          ) : (
            <div className="px-3 py-2 bg-purple-50 rounded-lg">
              <p className="text-xs text-purple-600 font-medium mb-0.5">Assigned Route</p>
              <p className="text-sm font-semibold text-purple-800">{routes[0]?.name || 'No route assigned'}</p>
            </div>
          )}

          {/* Location name */}
          <div>
            <label className="block text-sm font-medium mb-1">Location Name</label>
            <input type="text" value={addForm.name}
              onChange={e => setAddForm(f => ({...f, name: e.target.value}))}
              placeholder="e.g. Main Gate, Ravi's House, Temple Junction"
              className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"/>
          </div>

          {/* GPS capture */}
          <div>
            <label className="block text-sm font-medium mb-1">GPS Location</label>
            {addForm.latitude && addForm.longitude ? (
              <div className="flex items-center justify-between px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
                <div>
                  <p className="text-sm text-green-700 font-medium">Location captured</p>
                  <p className="text-xs font-mono text-green-600">{addForm.latitude}, {addForm.longitude}</p>
                </div>
                <button onClick={getLocation} disabled={gpsLoading}
                  className="text-xs text-green-700 underline hover:no-underline disabled:opacity-50">
                  Re-capture
                </button>
              </div>
            ) : (
              <button onClick={getLocation} disabled={gpsLoading}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-purple-300 rounded-lg text-sm text-purple-600 hover:bg-purple-50 disabled:opacity-50 transition-colors">
                <Navigation size={16}/>
                {gpsLoading ? 'Getting your location...' : 'Capture My Current GPS Location'}
              </button>
            )}
          </div>

          {!isAdmin && (
            <p className="text-xs text-gray-400">
              This pickup point will be submitted for admin approval. You can edit the name before approval.
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
          <button onClick={() => setAddModal(false)} className="px-4 py-1.5 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={savePoint}
            className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">
            {isAdmin ? 'Save' : 'Submit for Approval'}
          </button>
        </div>
      </Modal>

      {/* ── Edit Name Modal (Driver) ── */}
      <Modal isOpen={!!editModal} onClose={() => setEditModal(null)} title="Edit Location Name">
        {editModal && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Location Name</label>
              <input type="text" value={editModal.name}
                onChange={e => setEditModal(m => ({...m, name: e.target.value}))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"/>
            </div>
            <p className="text-xs text-gray-400 font-mono">{editModal.latitude}, {editModal.longitude}</p>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => setEditModal(null)} className="px-4 py-1.5 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={saveEdit} className="px-4 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700">Update</button>
        </div>
      </Modal>

      {/* ── Approve Modal (Admin) ── */}
      <Modal isOpen={!!approveModal} onClose={() => setApproveModal(null)} title="Approve Pickup Point">
        {approveModal && (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg space-y-1">
              <p className="text-sm font-semibold">{approveModal.name}</p>
              <p className="text-xs text-gray-500 font-mono">{approveModal.latitude}, {approveModal.longitude}</p>
              <p className="text-xs text-gray-500">Route: {approveModal.routeName} · Driver: {approveModal.driverName || '—'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Location Name <span className="text-gray-400 font-normal">(optional edit)</span></label>
              <input type="text" value={approveForm.name}
                onChange={e => setApproveForm(f => ({...f, name: e.target.value}))}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"/>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Monthly Transport Fee (₹) <span className="text-red-500">*</span></label>
              <input type="number" min="0" value={approveForm.transportFee}
                onChange={e => setApproveForm(f => ({...f, transportFee: e.target.value}))}
                placeholder="e.g. 500"
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"/>
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
          <button onClick={() => setApproveModal(null)} className="px-4 py-1.5 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={approvePoint}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
            <Check size={15}/> Approve
          </button>
        </div>
      </Modal>

      {/* ── Assign Student Modal (Admin) ── */}
      <Modal isOpen={!!assignModal} onClose={() => setAssignModal(null)} title="Assign Student to Pickup Point">
        {assignModal && (
          <div className="space-y-3">
            <div className="p-3 bg-purple-50 rounded-lg">
              <p className="text-sm font-semibold text-purple-800">{assignModal.name}</p>
              <p className="text-xs text-purple-600">Route: {assignModal.routeName} · Fee: ₹{assignModal.transportFee}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Search Student</label>
              <input type="text" value={studentSearch}
                onChange={e => setStudentSearch(e.target.value)}
                placeholder="Type student name..."
                className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300 mb-2"/>
              <select value={assignStudentId} onChange={e => setAssignStudentId(e.target.value)}
                size={6}
                className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300">
                <option value="">— Select student —</option>
                {filteredStudents.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.firstName} {s.lastName} · {s.admissionNumber || s.id}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">{filteredStudents.length} student(s) shown</p>
            </div>
            <p className="text-xs text-gray-500">
              This will update the student's transport route and stop, and send a notification to their parents.
            </p>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => { setAssignModal(null); setStudentSearch(''); }}
            className="px-4 py-1.5 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={assignStudent}
            className="flex items-center gap-1.5 px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700">
            <Users size={15}/> Assign
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default PickupPoints;
